import { bound } from "@frui.ts/helpers";
import { ConductorOneChildActive, watchBusy } from "@frui.ts/screens";
import { Router } from "@frui.ts/screens/dist";
import BusyWatcher from "@frui.ts/screens/dist/structure/busyWatcher";
import { action, observable } from "mobx";
import React from "react";
import LocalizationService from "services/localizationService";
import { CHARGE_NOT_FOUND } from "../../repositories/chargeRepository";
import EnumService from "../../services/enumService";
import EventBus, { chargeValidationFailed } from "../../services/eventBus";
import AppRouter from "services/appRouter";
import VehicleInfoViewModel from "../common/vehicleInfoViewModel";
import ChargeManagementEditViewModel from "./chargeManagementEdit/chargeManagementEditViewModel";
import { attachAutomaticBlurValidator } from "../../utils/validationPatch";
import { LicensePlateValidationRules } from "../../models/orderItem";
import Vignette, { AuthCodeValidationRules } from "../../models/vignette";
import { validateAll } from "@frui.ts/validation";
import EshopError from "../../models/eshopError";
import ChargeManagementService from "../../services/chargeManagementService";
import { czechID } from "../../config";
import * as Sentry from "@sentry/react";
import CountryResponse from "entities/countryResponse";
import { TurnstileInstance } from "@marsidev/react-turnstile";
import TurnstileViewModel from "viewModels/common/turnstileViewModel";
import { scrollToFirstError } from "utils/scrollTo";

export const ChargeManagementModuleNavigationName = "charge_management";

@Router.registerRoute({
  route: ChargeManagementModuleNavigationName,
  name: Router.Self,
  children: [ChargeManagementEditViewModel],
})
export default class ChargeManagementViewModel extends ConductorOneChildActive<ChargeManagementEditViewModel> {
  @observable vignette: Vignette;
  @observable errorMessage?: string;
  @observable tooltipUpdate = true;
  @observable formElement?: HTMLFormElement;
  busyWatcher = new BusyWatcher();
  private chargeManagementEditViewModel: ChargeManagementEditViewModel;
  @observable vehicleInfoViewModel: VehicleInfoViewModel;
  @observable turnstileViewModel: TurnstileViewModel;
  turnstileRef = React.createRef<TurnstileInstance>();
  @observable isTurnstileVisible?: boolean = false;
  vm: any;

  constructor(
    public localizationService: LocalizationService,
    private chargeManagementService: ChargeManagementService,
    private chargeManagementEditViewModelFactory: () => ChargeManagementEditViewModel,
    private vehicleInfoViewModelFactory: ReturnType<typeof VehicleInfoViewModel.Factory>,
    private turnstileViewModelFactory: ReturnType<typeof TurnstileViewModel.Factory>,
    private enumService: EnumService,
    private eventBus: EventBus,
    private router: AppRouter
  ) {
    super();
    this.navigationName = ChargeManagementModuleNavigationName;
    this.nameValue = "chargeManagement.title";

    this.reset();
    this.turnstileViewModel = this.turnstileViewModelFactory("chargeManagement", false);
    this.vignette.vehicle.countryId = czechID;
    this.vehicleInfoViewModel = this.vehicleInfoViewModelFactory(this.vignette.vehicle, {
      attachValidator: true,
      fixedLicensePlateSize: false,
      loadCountriesOnInit: true,
    });
    this.eventBus.subscribe(chargeValidationFailed, this.validationFailed);
  }

  @bound translate(code: string) {
    return this.localizationService.translateGeneral(`charge.management.${code}`);
  }

  @bound translateTurnstile(code: string) {
    return this.localizationService.translateGeneral(`turnstile.${code}`);
  }

  closeChild(child: ChargeManagementEditViewModel, forceClose = false) {
    this.reset();
    this.vehicleInfoViewModel = this.vehicleInfoViewModelFactory(this.vignette.vehicle);

    return super.closeChild(child, forceClose);
  }

  async navigate(subPath: string, params: any) {
    if (!params) {
      return Promise.resolve(undefined);
    }

    // Support for charge management QR code deeplink
    if (params.countryCode) {
      params.countryId = (await this.enumService.lazyValues("countries")).find(
        (country: CountryResponse) => country.code?.toLowerCase() === params.countryCode.toLowerCase()
      )?.id;
    }

    const countryId = params.countryId;
    const licensePlate = params.licensePlate;
    const authCode = params.authCode;

    if (authCode) {
      this.vignette.authCode = authCode;
    }

    if (licensePlate) {
      this.vignette.vehicle.licensePlate = licensePlate;
    }

    if (countryId) {
      this.vignette.vehicle.countryId = countryId;
    }
    if (subPath === "edit" && this.children.length === 0) {
      this.children.push((this.chargeManagementEditViewModel = this.chargeManagementEditViewModelFactory()));
    }

    return super.navigate(subPath, params);
  }

  @action.bound
  reset() {
    this.vignette = new Vignette();

    attachAutomaticBlurValidator(this.vignette, AuthCodeValidationRules);
    attachAutomaticBlurValidator(this.vignette.vehicle, LicensePlateValidationRules);
  }

  @action.bound
  @watchBusy
  async verifyCharge() {
    this.errorMessage = undefined;

    if (!validateAll([this.vignette, this.vignette.vehicle])) return;

    const countryId = this.vignette.vehicle.countryId;
    const licensePlate = this.vignette.vehicle.licensePlate;
    const authCode = this.vignette.authCode;

    if (!countryId || !licensePlate || !authCode) return;

    if (this.turnstileViewModel.turnstileToken) {
      await this.actionVerifyCharge(this.turnstileViewModel.turnstileToken);
    }
  }

  @action.bound
  @watchBusy
  async actionVerifyCharge(turnstileToken: string) {
    const countryId = this.vignette.vehicle.countryId;
    const licensePlate = this.vignette.vehicle.licensePlate;
    const authCode = this.vignette.authCode;

    if (!countryId || !licensePlate || !authCode) return;

    const result = await this.chargeManagementService
      .getCharge(countryId, licensePlate, authCode, turnstileToken)
      .then(response => {
        if (response instanceof EshopError) {
          this.turnstileViewModel.reset();
          scrollToFirstError(this.formElement);
          return;
        }

        return { countryId, licensePlate, authCode };
      });

    this.router.navigate(ChargeManagementEditViewModel, undefined, result);
    this.turnstileViewModel.reset();
  }

  @bound
  protected validationFailed(error: { type: string; payload: { type: string } }) {
    this.errorMessage =
      error.payload.type === CHARGE_NOT_FOUND
        ? this.translate(`errors.${error.payload.type}`)
        : this.localizationService.translateGeneral("errors.generic");
  }
}
